import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { apiError, useT } from "../i18n";
import { apiJson } from "../lib/api";
import { HotspotPreview } from "./HotspotPreview";
import { Button, Input, SegmentedControl, Select, Switch } from "./ui";
import type { ProjectListItem, TourDetail } from "../../shared/api-types";
import type { HotspotSize, HotspotStyle, HotspotVariant } from "../../shared/hotspot-style";

/** Überschrift einer Einstellungsgruppe — Trennlinie statt Kastenrahmen. */
function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="border-t border-zinc-200 pt-6 first:border-0 first:pt-0 dark:border-zinc-800">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
        {title}
      </h4>
      {hint && <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">{hint}</p>}
      <div className="mt-4 space-y-5">{children}</div>
    </section>
  );
}

/**
 * Eine Einstellung: Beschriftung, darunter die Erklärung, darunter das Feld —
 * in Lesereihenfolge. Die Breite bleibt bewusst begrenzt: ein Auswahlfeld mit
 * vier kurzen Werten über die ganze Seite zu ziehen macht es nicht bedienbarer.
 */
function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block max-w-md">
      <span className="block text-sm font-medium text-zinc-800 dark:text-zinc-200">{label}</span>
      {hint && (
        <span className="mt-0.5 block text-xs text-zinc-500 dark:text-zinc-400">{hint}</span>
      )}
      <span className="mt-1.5 block">{children}</span>
    </label>
  );
}

/**
 * Kompakte Zeile: Beschriftung links, Bedienelement rechts. Für die kleinen
 * Hotspot-Schalter — vier gestapelte Felder für Punktgröße und Pulsieren
 * würden die Sektion aufblähen, obwohl jeder Wert in ein Wort passt.
 */
function CompactRow({
  label,
  hint,
  children,
  plain = false,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  /** Bedienelement ist kein Formularfeld (Schalter, Segmentauswahl) — dann kein <label> drumherum */
  plain?: boolean;
}) {
  const cls = "grid grid-cols-[1fr_auto] items-center gap-4 py-2.5";
  const inner = (
    <>
      <span>
        <span className="block text-sm font-medium text-zinc-800 dark:text-zinc-200">{label}</span>
        {hint && (
          <span className="mt-0.5 block text-xs text-zinc-500 dark:text-zinc-400">{hint}</span>
        )}
      </span>
      <span className="flex justify-end">{children}</span>
    </>
  );
  return plain ? <div className={cls}>{inner}</div> : <label className={cls}>{inner}</label>;
}

/**
 * Die Tour-Einstellungen als Formular — ohne Rahmen und ohne Kasten, damit sie
 * sowohl im Rundgang-Editor (Dialog) als auch auf der Tour-Seite (eigener Tab)
 * sitzen können. Bewusst in Sektionen aufgebaut — künftige Einstellungen
 * bekommen hier ihre Gruppe, statt über die App zu streuen.
 *
 * Zwei Zuschnitte: die Tour-Seite zeigt alles, der Editor-Dialog nur das
 * Optische. Name und Projekt gehören zur Verwaltung der Tour, nicht zum
 * Aussehen — im Editor wären sie ein Fremdkörper.
 */
export function TourSettingsForm({
  tour,
  onSaved,
  onCancel,
  variant = "appearance",
}: {
  tour: TourDetail;
  onSaved: () => Promise<unknown> | void;
  /** Abbrechen-Knopf — nur im Dialog, der Tab hat nichts zu schließen */
  onCancel?: () => void;
  /** „full“ mit Name und Projekt (Tour-Seite), „appearance“ nur optisch (Editor) */
  variant?: "full" | "appearance";
}) {
  const t = useT();
  const full = variant === "full";
  const [name, setName] = useState(tour.name);
  const [projectValue, setProjectValue] = useState(tour.project.id);
  const [limitValue, setLimitValue] = useState(
    tour.viewLimitDeg !== null ? String(tour.viewLimitDeg) : "",
  );
  const [hotspotStyle, setHotspotStyle] = useState<HotspotStyle>(tour.hotspotStyle);
  const [gyroButton, setGyroButton] = useState(tour.gyroButton);
  const [projects, setProjects] = useState<ProjectListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!full) return;
    let alive = true;
    apiJson<ProjectListItem[]>("/api/projects")
      .then((p) => alive && setProjects(p))
      .catch(() => alive && setProjects(null));
    return () => {
      alive = false;
    };
  }, [full]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiJson(`/api/tours/${tour.id}`, {
        method: "PATCH",
        json: {
          ...(full ? { name, ...(projectValue ? { projectId: projectValue } : {}) } : {}),
          viewLimitDeg: limitValue ? Number(limitValue) : null,
          hotspotStyle,
          gyroButton,
        },
      });
      await onSaved();
      setSaved(true);
    } catch (err) {
      setError(apiError(t, (err as Error).message));
    }
    setBusy(false);
  }

  // Gespeichert-Hinweis nur im Tab: der Dialog schließt sich stattdessen
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(timer);
  }, [saved]);

  return (
    <form onSubmit={(e) => void submit(e)} className={full ? "max-w-2xl" : ""}>
      <div className="space-y-6">
        {full && (
          <Section title={t.tour.sectionGeneral}>
            <Row label={t.tour.nameLabel}>
              <Input
                className="w-full"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setSaved(false);
                }}
                required
              />
            </Row>
            <Row label={t.dashboard.projectLabel}>
              <Select
                className="w-full"
                value={projectValue}
                onChange={(e) => setProjectValue(e.target.value)}
              >
                {projects === null ? (
                  <option value={tour.project.id}>{tour.project.name}</option>
                ) : (
                  projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))
                )}
              </Select>
            </Row>
          </Section>
        )}

        <Section title={t.tour.sectionDisplay}>
          <Row label={t.tour.viewLimit} hint={t.tour.viewLimitHint}>
            <Select
              className="w-full"
              value={limitValue}
              onChange={(e) => setLimitValue(e.target.value)}
            >
              <option value="">{t.tour.viewLimitOff}</option>
              <option value="-75">{t.tour.viewLimitSoft}</option>
              <option value="-60">{t.tour.viewLimitMedium}</option>
              <option value="-45">{t.tour.viewLimitStrong}</option>
            </Select>
          </Row>
          {/* Der Knopf taucht ohnehin nur auf Touch-Geräten mit Sensor auf —
              hier geht es darum, ob er dort überhaupt angeboten wird. */}
          <div className="max-w-md">
            <CompactRow label={t.tour.gyroButton} hint={t.tour.gyroButtonHint} plain>
              <Switch
                checked={gyroButton}
                label={t.tour.gyroButton}
                onChange={(on) => {
                  setGyroButton(on);
                  setSaved(false);
                }}
              />
            </CompactRow>
          </div>
        </Section>

        <Section title={t.tour.sectionHotspots} hint={t.tour.hotspotHint}>
          {/* Vorschau vor den Feldern, nicht daneben: so ist zu sehen, was die
              nächste Auswahl verändert — und es braucht keinen freien Platz
              rechts, den es auf dem Handy und im Dialog ohnehin nicht gibt. */}
          <div className="max-w-md">
            <HotspotPreview style={hotspotStyle} scenes={tour.scenes} />
            <div className="mt-2 divide-y divide-zinc-100 dark:divide-zinc-800/80">
              <CompactRow label={t.tour.hotspotVariant}>
                <Select
                  className="w-44"
                  value={hotspotStyle.variant}
                  onChange={(e) =>
                    setHotspotStyle({ ...hotspotStyle, variant: e.target.value as HotspotVariant })
                  }
                >
                  <option value="dot">{t.tour.hotspotVariantDot}</option>
                  <option value="ring">{t.tour.hotspotVariantRing}</option>
                  <option value="arrow">{t.tour.hotspotVariantArrow}</option>
                  <option value="pill">{t.tour.hotspotVariantPill}</option>
                </Select>
              </CompactRow>
              <CompactRow label={t.tour.hotspotSize} plain>
                <SegmentedControl
                  name="hotspot-size"
                  label={t.tour.hotspotSize}
                  value={hotspotStyle.size}
                  options={[
                    { value: "s", label: t.tour.hotspotSizeS },
                    { value: "m", label: t.tour.hotspotSizeM },
                    { value: "l", label: t.tour.hotspotSizeL },
                  ]}
                  onChange={(size: HotspotSize) => setHotspotStyle({ ...hotspotStyle, size })}
                />
              </CompactRow>
              <CompactRow label={t.tour.hotspotColor}>
                {/* Der native Farbwähler ist als nackte Fläche kaum zu erkennen —
                    deshalb als Kachel mit Hex-Wert im Feld-Gewand der Zeile. */}
                <span className="flex h-9 items-center gap-2.5 rounded-lg border border-zinc-200 px-2.5 dark:border-zinc-700">
                  <input
                    type="color"
                    value={hotspotStyle.color}
                    onChange={(e) => setHotspotStyle({ ...hotspotStyle, color: e.target.value })}
                    className="color-swatch size-5 shrink-0"
                  />
                  <span className="font-mono text-xs uppercase text-zinc-500 dark:text-zinc-400">
                    {hotspotStyle.color}
                  </span>
                </span>
              </CompactRow>
              <CompactRow label={t.tour.hotspotPulse} plain>
                <Switch
                  checked={hotspotStyle.pulse}
                  label={t.tour.hotspotPulse}
                  onChange={(pulse) => setHotspotStyle({ ...hotspotStyle, pulse })}
                />
              </CompactRow>
            </div>
          </div>
        </Section>
      </div>

      {error && <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="mt-6 flex items-center justify-end gap-3 border-t border-zinc-200 pt-4 dark:border-zinc-800">
        {saved && !onCancel && (
          <span className="text-sm text-zinc-500 dark:text-zinc-400">{t.tour.settingsSaved}</span>
        )}
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t.common.cancel}
          </Button>
        )}
        <Button type="submit" disabled={busy}>
          {t.common.save}
        </Button>
      </div>
    </form>
  );
}
